import {
  AtIconInformative,
  AtIconInformativeProps,
  AtEyebrowLink,
  AtEyebrowLinkProps,
  Group,
  AtIcon,
} from '../../atoms'
import { PortableText, PortableTextProps } from '@portabletext/react'
import { Media } from '../or-media'
import dynamic from 'next/dynamic'

export interface OrProductDescriptionBlockProps {
  title: string
  subtitle?: string
  subtitles?: string[]
  body: PortableTextProps['value']
  informative: AtIconInformativeProps[]
  type: AtEyebrowLinkProps
  location?: AtEyebrowLinkProps
  images: Media[]
  floorPlanImage?: Media
  building: string
}

const DynamicImageGridLoader = dynamic(() => import('../../molecules').then((mod) => mod.MlImageGrid), {
  ssr: false,
  loading: () => (
    <div className="h-80 bg-gray-200 animate-pulse flex items-center justify-center">
      <div className="w-[50px] h-[50px] animate-spin rounded-full border-2 border-current border-t-transparent" />
    </div>
  ),
})

export const OrProductDescriptionBlock = ({
  title,
  subtitle,
  subtitles,
  images,
  floorPlanImage,
  body,
  informative,
  type,
  location,
  building,
}: OrProductDescriptionBlockProps) => {
  // Support both old subtitle (string) and new subtitles (array) for backward compatibility
  const displaySubtitles = subtitles && subtitles.length > 0 ? subtitles : subtitle ? [subtitle] : []

  const getIcon = (subtitle: string) => {
    if (subtitle.includes('Size')) {
      return <AtIcon className="" src={'/square-footage.svg'} />
    }
    return <Group />
  }

  return (
    <section>
      <DynamicImageGridLoader media={images} floorPlanImage={floorPlanImage} />
      <div className="px-4 sm:px-8 slg:px-12 lg:px-0 lg:w-[42.5rem]">
        <div className="mt-6">
          <AtEyebrowLink {...type} />
          {location ? (
            <>
              <span className="font-bold text-neutrals-800 text-12 uppercase"> at </span>
              <AtEyebrowLink {...location} />
            </>
          ) : (
            <span className="font-bold text-neutrals-800 text-12 uppercase"> at {building}</span>
          )}
        </div>
        <div className="flex flex-row items-start mt-2">
          <h1
            id="or-product-description-block-title"
            className="font-heading font-medium text-32 lg:text-[3.5rem] leading-1.3 lg:leading-1.4 text-neutrals-900 mb-2 lg:mb-2"
          >
            {title}
          </h1>
        </div>
        {displaySubtitles.length > 0 && (
          <div className="flex flex-col gap-2 mb-2 lg:mb-10">
            {displaySubtitles.map((subtitleText, index) => (
              <div key={index} className={`text-neutrals-800 font-bold inline-flex gap-2 py-1`}>
                <div className="w-10 h-10 p-2 shrink-0">{getIcon(subtitleText)}</div>
                <div className="leading-1.5 pt-2.5">
                  <p className="font-bold">{subtitleText}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div
          id="or-product-description-block-body"
          className="mt-6 text-14 lg:text-16 text-neutrals-800 leading-1.5 lg:mb-16 lg:mt-0 md:pr-24"
        >
          <PortableText value={body} />
        </div>
        <div id="or-product-description-block-features" className="mt-6 flex flex-col lg:gap-8 gap-4">
          {informative?.map((content, index) => (
            <AtIconInformative key={index} {...content} />
          ))}
        </div>
      </div>
    </section>
  )
}
